import { P, Section as KitSection, H3, Checks, Nots, Warn, Ext, Steps } from "./IAArticleKit";

/* IndianAirports — Visiting India: complete guide for foreign travelers
   (owner-provided copy, Sept 2026). Standalone SEO page /india/guides/visiting-india. */

const Section = (props) => <KitSection tid="visit-listen" {...props} />;

const EVISA_CATEGORIES = [
  "e-Tourist Visa", "e-Business Visa", "e-Medical Visa", "e-Medical Attendant Visa",
  "e-Student Visa", "e-Family Visa", "e-Transit Visa", "e-Miscellaneous Visa",
];

const EVISA_REQS = [
  "A passport from an eligible country or region", "At least six months of passport validity when applying",
  "At least two blank passport pages", "A separate passport for every traveler, including children",
  "A return or onward ticket", "Sufficient funds for the stay", "A recent compliant photograph",
  "A clear scan of the passport information page", "Purpose-specific supporting documents",
  "Entry through an authorized immigration checkpoint",
];

const DELAYS = [
  "Poor-quality passport scans", "Incorrect photographs", "Name or passport-number errors",
  "Incomplete travel history", "Payment problems", "Nationality or ancestry reviews",
  "Missing invitation or hospital documents", "Security checks", "Peak travel demand",
];

const ETA_CHECKS = [
  "Confirm that the status is GRANTED.", "Check the traveler's name.", "Check passport number.",
  "Check nationality.", "Check date of birth.", "Check photograph.", "Check visa category.",
  "Check validity and first-entry dates.", "Check authorized entry points.",
  "Print the ETA and retain a digital copy.",
];

const TOURIST_NOT = [
  "Paid employment", "Regular business operations", "Journalism", "Long-term academic study",
  "Missionary work", "Professional research", "Film production requiring approval",
  "Extended NGO work", "Activities requiring a special permit",
];

const BIZ_ACTIVITIES = [
  "Business meetings", "Trade discussions", "Contract negotiations", "Establishing commercial contacts",
  "Attending approved exhibitions", "Purchasing or selling industrial or commercial products",
];

const BIZ_DOCS = [
  "Business card", "Letter from the employer", "Invitation from an Indian company",
  "Indian company contact details", "Explanation of proposed activities",
  "Evidence of financial standing", "Travel itinerary",
];

const EMP_DOCS = [
  "Employment contract", "Appointment letter", "Employer registration documents",
  "Proof of professional qualifications", "Salary information", "Tax documents",
  "Undertaking from the employer", "FRRO registration after arrival",
];

const STUDENT_DOCS = [
  "Admission letter", "Institution details", "Course name and duration", "Proof of tuition payment",
  "Financial-support evidence", "Accommodation information", "Parental consent for minors",
  "Medical records", "FRRO documentation",
];

const SPECIALIZED = [
  "Journalism", "Professional media reporting", "Documentary production", "Commercial filming",
  "Research", "Conferences", "Missionary activities", "Mountaineering", "NGO work",
  "Religious outreach", "Tabligh activities", "Work involving foreign governments or diplomatic organizations",
  "Visits to protected or restricted areas",
];

const EARRIVAL_PREP = [
  "Passport information", "Nationality", "Flight details", "Port of arrival", "Arrival date",
  "Purpose of visit", "Address in India", "Local contact details", "Other requested travel declarations",
];

const EFRRO_SERVICES = [
  "Foreigner registration", "Visa extension", "Visa conversion where legally permitted",
  "Exit permit", "Address-related services", "Change of passport", "Loss of passport",
  "Other immigration permissions",
];

const RESTRICTED_REGIONS = [
  "Arunachal Pradesh", "Sikkim", "Ladakh", "Himachal Pradesh", "Uttarakhand", "Rajasthan",
  "Northeastern states", "Andaman and Nicobar Islands", "Other notified areas",
];

const CUSTOMS_ITEMS = [
  "Currency above applicable declaration limits", "Gold and jewelry", "Commercial goods",
  "High-value electronics", "Drones", "Satellite phones", "Radio or communication equipment",
  "Firearms and ammunition", "Narcotic or controlled substances", "Prescription medication",
  "Food, plants and seeds", "Animal products", "Antiques", "Wildlife products",
  "Maps or equipment involving sensitive areas",
];

const MISSIONS = [
  ["United States", "Washington, D.C.; New York; Chicago; Houston; San Francisco; Atlanta; Seattle"],
  ["Canada", "Ottawa; Toronto; Vancouver"],
  ["United Kingdom", "London; Birmingham; Edinburgh"],
  ["Ireland", "Dublin"],
  ["Australia", "Canberra; Sydney; Melbourne; Perth; Brisbane"],
  ["New Zealand", "Wellington; Auckland"],
  ["United Arab Emirates", "Abu Dhabi; Dubai"],
  ["Saudi Arabia", "Riyadh; Jeddah"],
  ["Gulf region", "Doha, Muscat, Kuwait City and Manama"],
  ["Singapore and Malaysia", "Singapore; Kuala Lumpur"],
  ["Europe", "Paris, Berlin, Frankfurt, Munich, Rome, Milan, Madrid, The Hague, Brussels, Vienna, Bern, Stockholm, Oslo, Copenhagen, Warsaw and Prague"],
  ["East Asia", "Beijing, Shanghai, Guangzhou, Hong Kong, Tokyo, Osaka-Kobe and Seoul"],
  ["Southeast Asia", "Bangkok, Jakarta, Hanoi, Ho Chi Minh City, Manila and Yangon"],
  ["Africa", "Pretoria, Johannesburg, Durban, Cape Town, Nairobi, Addis Ababa, Cairo, Lagos, Accra and Dar es Salaam"],
  ["Latin America", "Brasília, São Paulo, Buenos Aires, Mexico City, Santiago and Lima"],
  ["Russia and Central Asia", "Moscow, St. Petersburg, Astana, Tashkent and other regional capitals"],
];

const DOC_COPIES = [
  "Passport information page", "Indian visa or ETA", "Entry stamp", "e-Arrival acknowledgement",
  "Travel insurance", "Airline tickets", "Hotel bookings", "Emergency contacts",
  "Prescriptions", "Special permits",
];

const INSURANCE = [
  "Emergency medical care", "Hospitalization", "Medical evacuation", "Trip interruption",
  "Lost or delayed baggage", "Adventure activities", "Pre-existing conditions where necessary",
  "Motorcycle or scooter use if planned",
];

const RELIGIOUS = [
  "Follow posted dress requirements.", "Remove shoes where required.", "Cover the head when requested.",
  "Ask before taking photographs.", "Do not touch sacred objects.", "Remain quiet during worship.",
  "Respect separate entrances or restricted areas.",
  "Do not photograph cremations or private rituals without explicit permission.",
];

const EMERGENCY_SAVE = [
  "Local police", "Hotel", "Tour operator", "Travel insurer",
  "Your embassy or consulate", "Trusted local contact", "Bank card-loss numbers",
];

const DONTS = [
  "Do not overstay your visa — fines, detention, exit delays and future visa complications can follow; seek help from e-FRRO before the authorized stay expires",
  "Do not work on a tourist visa — paid work and many professional activities require the appropriate visa",
  "Do not conduct journalism, documentary or filming work as a tourist — these require the appropriate visa and permissions",
  "Do not photograph military bases, border installations, restricted airport zones, checkpoints, marked infrastructure, police operations or tribal communities without permission",
  "Do not carry illegal drugs — India has severe narcotics laws; never carry packages for another person",
  "Do not use drones without checking importation, registration and no-fly rules",
  "Do not bring a satellite phone without authorization — possession can result in detention or prosecution",
  "Do not participate in political demonstrations, rallies or protests",
  "Do not disrespect religious beliefs, figures, sacred objects or community practices",
  "Do not assume every public display of affection is appropriate — use discretion near religious sites and conservative communities",
  "Do not give money or valuables impulsively in crowded places — consider supporting a recognized local charity instead",
];

const REMOTE = [
  "Inform someone of the route.", "Use a reputable operator.", "Check weather.",
  "Carry emergency supplies.", "Confirm permits.", "Avoid driving at night when possible.",
  "Do not depend entirely on mobile coverage.",
];

const SCAMS = [
  "Unofficial taxi solicitation", "A driver claiming a hotel is closed",
  "A person directing tourists to a fake government office", "Counterfeit tour agencies",
  "Inflated transport or guide charges", "Card skimming", "SIM-card or identity misuse",
  "Friendship or romance scams", "Gem-export schemes", "Fake charity collection",
  "Unrequested help at ticket machines", "Pressure to buy tours, carpets or jewelry",
];

const WOMEN_SOLO = [
  "Arrange the first airport transfer in advance.", "Share vehicle and driver details with a trusted person.",
  "Avoid isolated areas after dark.", "Select well-reviewed accommodations.",
  "Sit near other women or families on public transport.", "Trust personal instincts and leave uncomfortable situations.",
  "Keep emergency contacts immediately accessible.",
];

const MONEY = [
  "Notify your bank of travel.", "Use ATMs attached to established banks.", "Shield the keypad.",
  "Avoid accepting help from strangers at an ATM.", "Keep transaction receipts.",
  "Check the currency and amount before approving a card payment.", "Carry modest cash for smaller businesses.",
  "Do not exchange money through unauthorized street dealers.", "Obtain a receipt from authorized foreign-exchange providers.",
];

const CULTURE = [
  "Greet people respectfully.", "Use the right hand when giving or receiving food or money where culturally appropriate.",
  "Remove footwear when entering a home or place of worship if asked.", "Avoid touching another person's head.",
  "Avoid pointing feet toward people or sacred objects.", "Ask before entering private religious areas.",
  "Respect queues even when they appear informal.", "Remain patient with bureaucratic or transportation delays.",
  "Learn a few polite words in the local language.", "Tip when appropriate, but check whether service is already included.",
];

const RESPONSIBLE = [
  "Do not litter.", "Reduce single-use plastic.", "Carry a reusable water bottle with an appropriate filter where practical.",
  "Do not feed or touch wildlife.", "Do not buy products made from endangered wildlife.",
  "Avoid exploitative animal attractions.", "Use authorized wildlife guides.",
  "Do not remove artifacts, stones or objects from protected sites.",
  "Support local craftspeople and legitimate community businesses.",
  "Ask permission before photographing tribal or rural communities.",
  "Avoid \"orphanage tourism\" and unverified child-focused volunteering.",
  "Follow park, monument and archaeological-site rules.",
];

const CONTACTS = [
  ["National emergency response", "Dial 112 where available", null],
  ["Immigration registration, extension or exit permit", "e-FRRO", "https://indianfrro.gov.in/eservices/"],
  ["Indian e-Visa assistance & arrival information", "India Visa Online — official portal", "https://indianvisaonline.gov.in"],
  ["Protected or restricted areas", "MHA PAP/RAP guidance", "https://www.mha.gov.in"],
  ["Indian missions abroad & consular services", "Ministry of External Affairs", "https://www.mea.gov.in"],
  ["Your country's emergency assistance", "Contact your own embassy or consulate in India", null],
  ["Lost passport", "Report to local police, contact your embassy and notify FRRO", null],
];

const CHECKLIST = [
  "Passport is valid for the required period.", "Passport has sufficient blank pages.",
  "Visa category matches the real purpose of travel.", "e-Visa ETA status shows GRANTED.",
  "Names and passport details are correct.", "Entry airport or seaport is authorized.",
  "Return or onward ticket is available.", "Accommodation is confirmed.",
  "e-Arrival Card is completed within 72 hours before arrival.",
  "Yellow fever certificate is carried when applicable.", "Protected-area permits are approved.",
  "Medication is in original packaging with prescriptions.", "Travel insurance is active.",
  "Passport and visa copies are stored separately.", "Airport transfer is arranged.",
  "Emergency and embassy contacts are saved.", "Customs restrictions have been reviewed.",
  "No prohibited drone, satellite phone or restricted item is packed.",
];

export default function IAVisitingIndiaArticle() {
  return (
    <article className="space-y-8" data-testid="india-visiting-article">
      <header className="space-y-3">
        <span className="inline-block text-[10px] font-800 uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1">Updated September 2026</span>
        <h2 className="font-heading font-900 text-2xl md:text-3xl text-zinc-900">
          Visiting India: A Complete Guide for Foreign Travelers
        </h2>
        <p className="text-sm font-700 text-zinc-500">Visa rules, arrival requirements, Indian missions abroad, local customs, safety, transportation and responsible travel</p>
        <P>
          India welcomes travelers with extraordinary cultural diversity, historic cities, sacred places, mountain landscapes,
          tropical beaches, wildlife, food traditions and modern business centers. A journey may include the monuments of
          Delhi and Agra, the palaces of Rajasthan, Kerala's backwaters, Goa's coastline, Mumbai's energy, Tamil Nadu's
          temples, Himalayan destinations or the wildlife reserves of central and northeastern India.
        </P>
        <P>
          Successful travel to India begins with the correct visa and careful preparation. Foreign nationals who are not
          Indian citizens, NRIs or OCI cardholders normally require a valid Indian visa or other legally recognized entry
          authorization before traveling.
        </P>
        <Warn>
          <b>Important:</b> This is general travel information, not immigration or legal advice. Visa eligibility and
          conditions depend on nationality, passport type, ancestry, purpose of travel and current government policy. Always
          verify the latest requirements through the official Government of India websites linked in this article.
        </Warn>
      </header>

      <Section title="Start with the correct visa">
        <P>
          A foreign traveler should obtain a visa matching the genuine primary purpose of the visit. A tourist visa should not
          be used for employment, journalism, long-term study, missionary activity or other purposes requiring a different
          category or government permission. India provides two principal application routes:
        </P>
        <Checks items={[
          "Electronic Visa (e-Visa) — applied for online through the official portal",
          "Regular or paper visa — issued through an Indian Embassy, High Commission or Consulate",
        ]} />
        <P>Use only Government of India websites: <Ext href="https://indianvisaonline.gov.in">India Visa Online — official gateway</Ext> (e-Visa, regular visa instructions, eligibility and the free e-Arrival Card all live here).</P>
        <Warn>
          The official e-Visa process is online and does not require an intermediary. Many commercial websites imitate
          official visa pages, charge unnecessary service fees or collect sensitive passport information. A web address ending
          in <b>.gov.in</b> is an important indicator of an official Indian government website.
        </Warn>
      </Section>

      <Section title="Indian e-Visa">
        <P>The e-Visa is available only to eligible passport holders and only for approved purposes. Current electronic categories on the official portal include:</P>
        <div className="flex flex-wrap gap-1.5">
          {EVISA_CATEGORIES.map((c) => (
            <span key={c} className="text-[11px] font-700 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2.5 py-1">{c}</span>
          ))}
        </div>
        <P>Not every category is available to every nationality. Confirm eligibility, validity, maximum stay, permitted entries and required documents on the official portal before applying.</P>
        <H3>Basic e-Visa requirements</H3>
        <Checks items={EVISA_REQS} />
        <P>
          Biometric information may be collected upon arrival. The e-Visa fee depends on nationality and category; processing
          fees are generally nonrefundable — including when an application is refused or a trip is cancelled.
        </P>
      </Section>

      <Section title="Apply early — and wait for GRANTED">
        <P>Do not wait until the day before departure. Processing can be delayed by:</P>
        <Checks items={DELAYS} />
        <Warn>
          Do not purchase a nonrefundable flight based only on submitting an application. Wait until the Electronic Travel
          Authorization (ETA) shows <b>GRANTED</b>, then verify every detail — an application acknowledgement, payment receipt
          or pending status does not authorize travel.
        </Warn>
        <H3>Before departure, verify your ETA</H3>
        <Steps items={ETA_CHECKS} />
        <H3>Authorized e-Visa entry points</H3>
        <P>
          An e-Visa holder must enter India through an airport or seaport currently approved for e-Visa entry (the official
          portal maintains the list). Departure is ordinarily allowed through authorized immigration checkpoints — but never
          use an e-Visa for initial entry through an unauthorized land border, airport or seaport.
        </P>
      </Section>

      <Section title="Tourist visa">
        <P>A tourist visa is intended for genuine tourism-related purposes: recreation, sightseeing, visiting friends and relatives, and short unpaid recreational activities permitted by the visa rules. A tourist should NOT use this visa for:</P>
        <Nots items={TOURIST_NOT} />
        <Warn>
          The expiration date of the visa is not always the same as the maximum permitted stay. Read the ETA and entry
          endorsement carefully — the number of permitted entries and maximum stay depend on the visa version and nationality.
        </Warn>
      </Section>

      <Section title="Business visa">
        <P>A business visa may cover qualifying activities such as:</P>
        <Checks items={BIZ_ACTIVITIES} />
        <P>Applicants may need:</P>
        <Checks items={BIZ_DOCS} />
        <P>
          A business visa is not automatically an employment visa — receiving a salary from an Indian employer or taking up
          regular employment may require an employment visa. Longer continuous stays may trigger FRRO registration
          requirements; follow the conditions printed on the visa and published by the Bureau of Immigration.
        </P>
      </Section>

      <Section title="Employment visa">
        <P>Foreign nationals taking employment in India generally require a regular employment visa rather than an e-Visa. Possible requirements include:</P>
        <Checks items={EMP_DOCS} />
        <Warn>
          Employment visas can carry minimum-salary, skill, employer-specific and sector-specific conditions. Do not enter as
          a tourist and begin working while attempting to convert status later — the official e-Visa FAQ states an e-Visa is
          not available for employment. Apply through the Indian Mission with jurisdiction over your residence.
        </Warn>
      </Section>

      <Section title="Student visa">
        <P>Students admitted to an Indian educational institution generally need the student visa or authorized electronic student category. Supporting records can include:</P>
        <Checks items={STUDENT_DOCS} />
        <P>Verify whether FRRO registration is required and complete it within the time stated on the visa or immigration endorsement.</P>
      </Section>

      <Section title="Medical and medical-attendant visas">
        <P>
          Foreign nationals traveling for treatment should use the appropriate medical visa. A medical application commonly
          requires a letter from the Indian hospital identifying the patient's name, passport number, nationality, proposed
          treatment and expected admission or appointment date. Eligible attendants should use the medical-attendant category
          rather than a tourist visa. Official medical and AYUSH-related services run through the{" "}
          <Ext href="https://indianvisaonline.gov.in">official visa portal</Ext>.
        </P>
      </Section>

      <Section title="Conference, journalism, research, film and missionary activities">
        <P>Certain activities require specialized visas, permissions or government clearances, including:</P>
        <Checks items={SPECIALIZED} />
        <Warn>
          Disclose the actual purpose. Carrying professional media equipment or presenting oneself as a tourist while
          conducting journalism can create serious immigration and legal problems. Ordinary e-Visas are not available for
          journalism or NGO activities, although narrowly defined short-duration voluntary work without payment may be
          permitted under stated conditions.
        </Warn>
        <H3>Applicants connected to Pakistan</H3>
        <P>
          Special eligibility and application procedures can apply to Pakistani passport holders, former Pakistani citizens,
          persons of Pakistani origin, and applicants whose parents or grandparents were born in or permanently resident in
          Pakistan — these applicants may not be eligible for the e-Visa route and may need a regular visa through an Indian
          Mission. Never conceal ancestry, prior nationality or travel history.
        </P>
      </Section>

      <Section title="Visa on Arrival">
        <P>
          India's Visa on Arrival program is limited — not a general facility for all tourists. The official portal currently
          identifies limited eligibility for nationals of <b>Japan</b>, <b>South Korea</b> and the <b>United Arab Emirates</b>{" "}
          (subject to additional prior-visa conditions), only for specified purposes and at designated airports.
        </P>
        <Warn>Completing an e-Arrival Card does not create Visa on Arrival eligibility. Official information: <Ext href="https://indianvisaonline.gov.in">India Visa on Arrival</Ext>.</Warn>
      </Section>

      <Section title="India e-Arrival Card">
        <P>
          Foreign nationals must complete India's official e-Arrival Card online <b>within 72 hours before arriving</b>,
          according to the Government of India's visa portal. It is free, an immigration-arrival information form, separate
          from the visa application, required in addition to a valid visa — and it is NOT a guarantee of admission, a passport
          replacement or a customs declaration.
        </P>
        <H3>Prepare</H3>
        <Checks items={EARRIVAL_PREP} />
        <Warn>Save the acknowledgement and keep a copy available offline. Avoid websites that charge a fee for this free government service — <Ext href="https://indianvisaonline.gov.in">complete it on the official portal</Ext>.</Warn>
      </Section>

      <Section title="Registration, extensions and immigration services">
        <P>
          Some foreign nationals must register with the FRRO/FRO after arrival, depending on visa category, endorsement,
          length of stay, nationality, age, purpose and entry conditions. Never assume registration is unnecessary merely
          because the visa is valid for a long period. India's <Ext href="https://indianfrro.gov.in/eservices/">e-FRRO system</Ext> handles:
        </P>
        <Checks items={EFRRO_SERVICES} />
        <Warn>
          The e-Visa is generally non-extendable and non-convertible, except where a specific legal exception applies. Do not
          remain in India beyond the authorized period while trying to solve the matter informally.
        </Warn>
        <H3>Hotel and accommodation registration</H3>
        <P>
          Hotels, guesthouses, hostels and other accommodators have reporting obligations for foreign guests and may
          legitimately ask to scan your passport, visa/ETA, entry stamp, arrival details and contact information. Travelers
          staying in a private home should ensure applicable foreigner-reporting requirements are followed.
        </P>
      </Section>

      <Section title="Protected and restricted areas">
        <P>Some regions require a Protected Area Permit, Restricted Area Permit or other special authorization — a standard visa or e-Visa is not sufficient by itself. Restrictions may apply in sensitive border, island, military or tribal areas, including parts of:</P>
        <Checks items={RESTRICTED_REGIONS} />
        <Warn>Do not enter military installations, cantonments, border roads or restricted islands without authorization. Official information: <Ext href="https://www.mha.gov.in">MHA PAP/RAP guidance</Ext>.</Warn>
        <H3>Yellow fever requirements</H3>
        <P>
          Travelers arriving within the applicable period after leaving or transiting a yellow-fever-risk country may be
          required to carry an original valid yellow fever vaccination certificate — failure to present it can lead to
          quarantine. Check the current list through India's Ministry of Health and the official e-Visa guidance.
        </P>
      </Section>

      <Section title="Customs and restricted goods">
        <P>The e-Arrival Card is not a customs declaration. Items requiring special attention include:</P>
        <Checks items={CUSTOMS_ITEMS} />
        <Warn>
          Satellite phones and certain communication devices can create particularly serious legal problems without advance
          authorization. Retain prescriptions and carry medicines in original labeled packaging — a medication lawfully
          prescribed abroad may still be restricted in India.
        </Warn>
      </Section>

      <Section title="Major Indian embassies, high commissions and consulates abroad">
        <P>
          India maintains missions worldwide; the office serving an applicant depends on legal residence and consular
          jurisdiction. Representative major locations (not exhaustive):
        </P>
        <div className="overflow-x-auto border border-zinc-200 rounded-2xl">
          <table className="w-full text-xs" data-testid="india-visiting-missions-table">
            <thead>
              <tr className="bg-zinc-50 text-left">
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Region</th>
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Principal Indian missions and consular locations</th>
              </tr>
            </thead>
            <tbody>
              {MISSIONS.map(([r, cities]) => (
                <tr key={r} className="border-t border-zinc-100 align-top">
                  <td className="px-3.5 py-2.5 font-700 text-zinc-800 whitespace-nowrap">{r}</td>
                  <td className="px-3.5 py-2.5 text-zinc-600">{cities}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <P>
          Locations, jurisdiction and services can change — use the official <Ext href="https://www.mea.gov.in">Ministry of External Affairs</Ext> country and mission information rather than an old address. Once in India, also know how to contact your own country's embassy or consulate for emergency assistance.
        </P>
      </Section>

      <Section title="Things visitors should do in India">
        <H3>Carry secure copies of important documents</H3>
        <Checks items={DOC_COPIES} />
        <P>Do not carry the only copy of every document in one bag. If available, register your itinerary with your country's embassy or travel-notification service and review its travel advisory.</P>
        <H3>Purchase appropriate travel insurance</H3>
        <Checks items={INSURANCE} />
        <P>Confirm whether cashless hospital treatment is available or whether payment must be made first.</P>
        <H3>Use safe drinking water</H3>
        <P>
          Drink sealed bottled water from a reliable source (check the seal) or properly treated water. Use caution with ice
          of uncertain origin, raw salads washed in untreated water, cut fruit left exposed, unpasteurized dairy and food
          stored at unsafe temperatures. Busy establishments with good turnover beat empty stalls.
        </P>
        <H3>Use licensed or reputable transportation</H3>
        <P>
          Airport prepaid taxi counters, hotel-arranged transportation, recognized taxi companies, reputable app-based
          services and official public transport. Confirm the destination and estimated price before starting when no meter or
          fixed fare applies; verify the vehicle registration and driver in the app.
        </P>
        <H3>Dress and behave appropriately at religious sites</H3>
        <Checks items={RELIGIOUS} />
        <H3>Ask before photographing people</H3>
        <P>Request consent before photographing individuals, children, ceremonies, religious figures or people in vulnerable circumstances.</P>
        <H3>Keep emergency contacts accessible</H3>
        <P>India's national emergency response number is commonly <b>112</b> (local availability can vary). Save:</P>
        <Checks items={EMERGENCY_SAVE} />
        <H3>Guides, bargaining and personal information</H3>
        <P>
          Use authorized guides at monuments, wildlife reserves and heritage sites — ask for identification and agree on price
          and inclusions in advance. Bargain respectfully where expected. Never casually hand strangers copies of your
          passport, visa, bank card, one-time passwords, itinerary or hotel room number; verify who is collecting documents
          and why.
        </P>
      </Section>

      <Section title="Things visitors should not do">
        <Nots items={DONTS} />
        <H3>Remote-area travel</H3>
        <P>In mountain, desert, forest, island and border regions:</P>
        <Checks items={REMOTE} />
      </Section>

      <Section title="Personal safety and common scams">
        <P>India is visited safely by millions of travelers, but preparation matters. Common problems can include:</P>
        <Checks items={SCAMS} />
        <P>Verify closures directly with the hotel or attraction — never let a stranger redirect you to an "official tourist office" without independent confirmation.</P>
        <H3>Women traveling alone may wish to</H3>
        <Checks items={WOMEN_SOLO} />
        <P>No safety strategy guarantees protection, but advance planning reduces exposure to avoidable risk.</P>
      </Section>

      <Section title="Money, cards and digital payments">
        <P>Carry more than one payment method. Practical steps:</P>
        <Checks items={MONEY} />
        <P>Some Indian digital-payment systems require an Indian-compatible bank or mobile arrangement — do not assume every QR code will work with an overseas payment app.</P>
      </Section>

      <Section title="Transportation">
        <H3>Flights</H3>
        <P>Allow generous time for traffic and airport security. Entry into many terminals requires a valid ticket and identification; the name on the ticket should match the passport.</P>
        <H3>Trains</H3>
        <P>Purchase tickets through official Indian Railways channels or reputable authorized providers. Keep the passport used for the reservation available. Watch baggage carefully and avoid accepting food or drinks from unknown persons.</P>
        <H3>Roads</H3>
        <P>Traffic can be unfamiliar and intense. Use seat belts and wear a proper helmet on two-wheelers. A foreign driving license may not be sufficient — check whether an International Driving Permit and local documentation are required. Avoid renting a motorcycle without appropriate licensing, experience and insurance.</P>
        <H3>Taxis and rideshare</H3>
        <P>Check the number plate, driver identity and destination before entering. Do not allow the driver to cancel an app trip and continue unofficially unless you fully understand the safety and payment consequences.</P>
      </Section>

      <Section title="Respectful cultural conduct">
        <P>India is not culturally uniform — customs vary by state, language, religion, community and generation. Helpful practices:</P>
        <Checks items={CULTURE} />
        <P>Visitors are not expected to know every custom. Courtesy, observation and a willingness to ask usually go a long way.</P>
      </Section>

      <Section title="Responsible tourism">
        <Checks items={RESPONSIBLE} />
      </Section>

      <Section title="Emergency and immigration contacts">
        <div className="overflow-x-auto border border-zinc-200 rounded-2xl">
          <table className="w-full text-xs" data-testid="india-visiting-contacts-table">
            <thead>
              <tr className="bg-zinc-50 text-left">
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Need</th>
                <th className="px-3.5 py-2.5 font-800 text-zinc-500 uppercase text-[10px]">Official resource</th>
              </tr>
            </thead>
            <tbody>
              {CONTACTS.map(([need, label, href]) => (
                <tr key={need} className="border-t border-zinc-100 align-top">
                  <td className="px-3.5 py-2.5 text-zinc-600">{need}</td>
                  <td className="px-3.5 py-2.5">{href ? <Ext href={href}>{label}</Ext> : <span className="font-700 text-zinc-700">{label}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Before-you-fly checklist">
        <div data-testid="india-visiting-checklist">
          <Steps items={CHECKLIST} color="bg-emerald-600" />
        </div>
      </Section>

      <Section title="Final word">
        <P>
          India rewards preparation. The most important rule is to enter with the correct visa, use official government
          websites, respect the permitted purpose and duration of stay, complete the e-Arrival Card, obtain special permits
          where necessary and remain sensitive to the country's laws and cultural diversity.
        </P>
        <P>
          A well-prepared visitor can focus on what makes India unforgettable: its people, landscapes, languages, food,
          architecture, faith traditions, innovation and remarkable variety from one region to the next.
        </P>
        <div className="bg-gradient-to-br from-orange-600 to-rose-700 text-white rounded-2xl p-5 text-center" data-testid="india-visiting-closing">
          <p className="text-sm font-800">Plan carefully. Travel respectfully.</p>
          <p className="mt-1 text-xs text-orange-100">Keep an open mind — and allow India to surprise you.</p>
        </div>
      </Section>
    </article>
  );
}
